import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import "./Services.css";
import Reveal from "../motion/Reveal";

/**
 * Services, answered from the visitor's side: the section asks "What sounds most like you?",
 * so each entry is a situation they recognise, and picking one shows what they would get,
 * where it has already been done, and a way to start.
 */
const SITUATIONS = [
  {
    id: "product",
    situation: "I have an idea and need the whole product built",
    service: "Full Stack Development",
    headline: "One engineer, from the database to the last screen.",
    description:
      "I design the data model, the API, the authentication and the interface together, so the product ships as one coherent system instead of pieces that have to be reconciled later.",
    delivers: [
      "Database schema and a documented REST API",
      "Sign-in, roles and server-side permissions",
      "A responsive React or Angular interface",
      "Deployment, and code that is easy to extend",
    ],
    proof: [
      { label: "ImmoFlow", to: "/projects/immoflow" },
      { label: "CRMBuzz", to: "/projects/crmbuzz" },
    ],
  },
  {
    id: "ai",
    situation: "My product needs an AI feature I can trust",
    service: "AI Integration",
    headline: "AI that fits your product, not a black box bolted on.",
    description:
      "I add language-model features behind a Generate → Preview → Accept flow: outputs are structured and validated, grounded in your own data, and never written straight to your database.",
    delivers: [
      "LLM features with validated, structured output",
      "Semantic search and RAG over your own content",
      "A review step that keeps a person in control",
      "Answers shown with the sources they came from",
    ],
    proof: [
      { label: "Aplivia", to: "/projects/aplivia" },
      { label: "CRMBuzz", to: "/projects/crmbuzz" },
      { label: "CodeQuery", to: "/projects/codequery" },
    ],
  },
  {
    id: "internal",
    situation: "Our internal processes still run on spreadsheets",
    service: "Full Stack Development",
    headline: "An internal platform shaped around how your team works.",
    description:
      "Workflows, approvals, roles and dashboards in one place — with the running state of every process visible, so nobody has to ask where a request is stuck.",
    delivers: [
      "Workflows and approvals modelled, then automated",
      "A dashboard for each role, from one codebase",
      "Notifications when something needs attention",
      "Data kept separate between teams or agencies",
    ],
    proof: [
      { label: "BPMFlow", to: "/projects/bpmflow" },
      { label: "ImmoFlow", to: "/projects/immoflow" },
    ],
  },
  {
    id: "team",
    situation: "I'm hiring an engineer for my team",
    service: "Available for hire",
    headline: "Someone who can own a feature end to end.",
    description:
      "I work across the stack — React or Angular, Java Spring Boot or Node.js, PostgreSQL or MongoDB, and Python for NLP — and I'm used to taking a feature from the schema to production.",
    delivers: [
      "Frontend and backend in the same pair of hands",
      "Experience adding AI features to real products",
      "Attention to deployment, monitoring and data integrity",
      "Quick to pick up an unfamiliar stack",
    ],
    proof: [
      { label: "BPMFlow", to: "/projects/bpmflow" },
      { label: "Aplivia", to: "/projects/aplivia" },
    ],
  },
];

const Services = () => {
  const [active, setActive] = useState(0);
  const reducedMotion = useReducedMotion();
  const current = SITUATIONS[active];

  // Up/Down (and Left/Right) move between situations, as in any tab list.
  const onKeyDown = (e) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (active + step + SITUATIONS.length) % SITUATIONS.length;
    setActive(next);
    document.getElementById(`situation-tab-${SITUATIONS[next].id}`)?.focus();
  };

  return (
    <Reveal as="section" id="services">
      <h5 className="title">What sounds most like you?</h5>
      <h2>Services</h2>

      <div className="container fit">
        <div className="fit__list" role="tablist" aria-label="Your situation" aria-orientation="vertical">
          {SITUATIONS.map((s, i) => (
            <button
              key={s.id}
              id={`situation-tab-${s.id}`}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-controls="situation-panel"
              tabIndex={i === active ? 0 : -1}
              className={`fit__option${i === active ? " is-active" : ""}`}
              onClick={() => setActive(i)}
              onKeyDown={onKeyDown}
            >
              <span className="fit__quote" aria-hidden="true">“</span>
              <span className="fit__situation">{s.situation}</span>
              <FaArrowRight className="fit__arrow" aria-hidden="true" />
            </button>
          ))}
        </div>

        <div
          className="fit__panel"
          id="situation-panel"
          role="tabpanel"
          aria-labelledby={`situation-tab-${current.id}`}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={current.id}
              className="fit__content"
              initial={reducedMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <span className="fit__service">{current.service}</span>
              <h3 className="fit__headline">{current.headline}</h3>
              <div className="fit__description">{current.description}</div>

              <ul className="fit__delivers">
                {current.delivers.map((item) => (
                  <li key={item}>
                    <span className="fit__check" aria-hidden="true"><FaCheck /></span>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="fit__footer">
                <div className="fit__proof">
                  <span className="fit__proof-label">Already done in</span>
                  {current.proof.map((p) => (
                    <Link key={p.to} to={p.to} className="fit__proof-link">{p.label}</Link>
                  ))}
                </div>
                <a href="#contact" className="btn fit__cta">
                  Let's talk about it <FaArrowRight size={11} aria-hidden="true" />
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Reveal>
  );
};

export default Services;
